/**
 * A programme's curriculum history, loaded when its row is opened.
 *
 * The working change cases are a per-programme read, so loading them for
 * every row would be one request per programme on a screen most readers only
 * scan. They load on expand, once, and stay loaded.
 *
 * @module components/portfolio/useCurriculumHistory
 */

import { useCallback, useRef, useState } from "react";

import type { AsyncState } from "@/components/ui";
import type {
  ProgrammeGovernanceSummary,
  ProgrammePlatformClient,
  SessionContext,
} from "@/platform";
import { begin } from "@/utils/begin";

export interface UseCurriculumHistoryResult {
  expandedKeys: ReadonlySet<string>;
  toggle: (programmeId: string) => void;
  /** Reads a programme's history once; `retry` reads a failed one again. */
  open: (programmeId: string) => void;
  /** Reads a row again after its read failed, leaving it open. */
  retry: (programmeId: string) => void;
  stateFor: (programmeId: string) => AsyncState<ProgrammeGovernanceSummary | null>;
}

/**
 * Whether this account may see a programme's unapproved work.
 *
 * An admin sees every programme in the tenant; anyone else sees only the ones
 * they hold a responsibility on. Without this the history would show the
 * approved revisions to everyone and fail the governance read for most.
 */
export function canViewWorkingHistory(
  session: SessionContext | undefined,
  programmeId: string,
): boolean {
  if (!session) {
    return false;
  }
  if (
    session.subject.accountRole === "client-admin" ||
    session.subject.accountRole === "platform-admin"
  ) {
    return true;
  }
  return session.subject.responsibilities.some(
    (responsibility) => responsibility.programmeId === programmeId,
  );
}

export function useCurriculumHistory(
  client: ProgrammePlatformClient,
  session: SessionContext | undefined,
): UseCurriculumHistoryResult {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [states, setStates] = useState<
    Record<string, AsyncState<ProgrammeGovernanceSummary | null>>
  >({});
  // Per programme, because rows expand independently and their reads are
  // genuinely concurrent. One counter for all of them made each newly opened
  // row cancel the one before it, and a discarded reply left that row saying
  // "Loading" with nothing to retry it.
  const generations = useRef(new Map<string, number>());

  const load = useCallback(
    (programmeId: string) => {
      const mine = (generations.current.get(programmeId) ?? 0) + 1;
      generations.current.set(programmeId, mine);
      setStates((current) => ({ ...current, [programmeId]: { status: "loading" } }));
      begin(() => client.getProgrammeGovernance(programmeId))
        .then((data) => {
          if (mine !== generations.current.get(programmeId)) {
            return;
          }
          setStates((current) => ({ ...current, [programmeId]: { status: "ready", data } }));
        })
        .catch((cause: unknown) => {
          if (mine !== generations.current.get(programmeId)) {
            return;
          }
          setStates((current) => ({
            ...current,
            [programmeId]: {
              status: "error",
              error: cause instanceof Error ? cause : new Error("Unable to load working revisions"),
            },
          }));
        });
    },
    [client],
  );

  const open = useCallback(
    (programmeId: string) => {
      // Someone who cannot see working revisions still gets the approved
      // history, which needs no read at all.
      if (!canViewWorkingHistory(session, programmeId)) {
        // Unchanged when already recorded: `open` depends on `states`, and a
        // caller that opens from an effect would otherwise re-run it forever.
        setStates((current) =>
          current[programmeId]
            ? current
            : { ...current, [programmeId]: { status: "ready", data: null } },
        );
        return;
      }
      if (states[programmeId]) {
        return;
      }
      load(programmeId);
    },
    [load, session, states],
  );

  const toggle = useCallback(
    (programmeId: string) => {
      // From the rendered set, not from inside the updater below. React calls
      // an updater during the render rather than at the call, and evaluates it
      // eagerly only while the queue is empty — so a variable assigned in
      // there held for the first toggle and read `false` for every one after.
      const opening = !expanded.has(programmeId);
      setExpanded((current) => {
        const next = new Set(current);
        if (opening) {
          next.add(programmeId);
        } else {
          next.delete(programmeId);
        }
        return next;
      });
      if (opening) {
        open(programmeId);
      }
    },
    [expanded, open],
  );

  const stateFor = useCallback(
    (programmeId: string): AsyncState<ProgrammeGovernanceSummary | null> =>
      states[programmeId] ?? { status: "ready", data: null },
    [states],
  );

  return { expandedKeys: expanded, toggle, open, retry: load, stateFor };
}
